import {useState, useEffect, useMemo} from "react"
import Geometry from "ol/geom/Geometry"
import LandController from "./LandController"
import {parseBoundaryMap} from "./boundaryGeo"

export interface UseBoundariesResult {
    // landId -> boundary GeoJSON string (as returned by the backend)
    boundaryMap: Record<number, string>
    // landId -> parsed OpenLayers geometry (EPSG:4326), multipart-aware
    geometryMap: Record<number, Geometry>
    loading: boolean
    error: boolean
}

/**
 * Hook for fetching boundary geometry for multiple lands.
 * Returns the raw GeoJSON keyed by landId plus parsed OpenLayers geometries for
 * rendering, point matching and area calculations.
 */
export function useBoundaries(landIds: number[] | undefined): UseBoundariesResult {
    const [boundaryMap, setBoundaryMap] = useState<Record<number, string>>({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    // Stable key for dependency comparison
    const key = landIds?.slice().sort().join(',') ?? ''

    useEffect(() => {
        if (!landIds || landIds.length === 0) {
            setBoundaryMap({})
            setLoading(false)
            setError(false)
            return
        }

        let cancelled = false
        setLoading(true)
        setError(false)

        LandController.boundaries(landIds)
            .then(result => {
                if (!cancelled) {
                    setBoundaryMap(result)
                    setLoading(false)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setBoundaryMap({})
                    setError(true)
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [key])

    const geometryMap = useMemo(() => parseBoundaryMap(boundaryMap), [boundaryMap])

    return {boundaryMap, geometryMap, loading, error}
}
